import { useEffect, useState } from "react";
import {
  CARPET_TYPES,
  ITEM_TYPE_LABELS_FA,
  SERVICE_GROUPS,
  SERVICE_LABELS_FA,
  SYSTEM_ROLE_KEYS,
  type BusinessCatalogResponse,
  type BusinessContextResponse,
  type BusinessLogisticsSettings,
  type BusinessProfileResponse,
  type BusinessReportsSummary,
  type BusinessServiceOffering,
  type BusinessTeamResponse,
  type CarpetType,
  type PriceUnit,
  type ServiceCatalogKey,
  type SystemRoleKey,
} from "@ghalishor/contracts";
import type { IdentityApiClient } from "./auth/api-client";
import { identityErrorMessage } from "./auth/api-client";
import { IDENTITY_WEB_ROUTES, type IdentityWebRoute } from "./auth/web-flow";

const UNIT_LABELS: Record<PriceUnit, string> = {
  per_sqm: "مترمربع",
  per_item: "هر تخته",
  flat: "مقطوع",
  per_floor: "هر طبقه",
};

function hasCap(
  context: BusinessContextResponse | null,
  capability: string,
): boolean {
  return context?.effectiveCapabilities.includes(capability as never) === true;
}

export function ConsoleNav(props: {
  readonly current: IdentityWebRoute;
  readonly onNavigate: (route: IdentityWebRoute) => void;
}) {
  const items: Array<{
    route: IdentityWebRoute;
    label: string;
    description: string;
  }> = [
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleReports,
      label: "گزارشات",
      description: "خلاصه عملکرد، سفارش‌ها و شاخص‌ها",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleRates,
      label: "خدمات و نرخ‌ها",
      description: "تعریف خدمات قابل ارائه و قیمت‌گذاری",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleLogistics,
      label: "حمل و طبقات",
      description: "قواعد جمع‌آوری، حمل و هزینه طبقات",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleFleet,
      label: "ناوگان و مسیرها",
      description: "رانندگان، موقعیت‌ها و برنامه مسیر",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleTeam,
      label: "تیم و نقش‌ها",
      description: "کاربران قالیشویی و سطح دسترسی",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessMessages,
      label: "🔔 پیام‌ها و اعلان‌ها",
      description: "گفت‌وگو با مشتری، تیم و پلتفرم",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleProfile,
      label: "پروفایل عملیاتی",
      description: "محدوده خدمت و اطلاعات عملیاتی",
    },
    {
      route: IDENTITY_WEB_ROUTES.businessConsoleSettings,
      label: "تنظیمات",
      description: "شیوه پذیرش و تنظیمات کسب‌وکار",
    },
  ];
  const links = (
    <div className="console-navigation__grid">
      {items.map((item) => (
        <button
          key={item.route}
          type="button"
          className={props.current === item.route ? "is-current" : ""}
          aria-current={props.current === item.route ? "page" : undefined}
          onClick={() => props.onNavigate(item.route)}
        >
          <strong>{item.label}</strong>
          <small>{item.description}</small>
        </button>
      ))}
    </div>
  );
  return (
    <nav className="console-navigation" aria-label="بخش‌های کنسول مدیریت">
      <div className="console-navigation__location">
        <span>
          داشبورد قالیشویی ← کنسول مدیریت
          {props.current !== IDENTITY_WEB_ROUTES.businessConsole
            ? ` ← ${items.find((item) => item.route === props.current)?.label ?? "بخش داخلی"}`
            : ""}
        </span>
        <button
          type="button"
          onClick={() => props.onNavigate(IDENTITY_WEB_ROUTES.businessHome)}
        >
          بازگشت به داشبورد قالیشویی
        </button>
      </div>
      {props.current === IDENTITY_WEB_ROUTES.businessConsole ? (
        links
      ) : (
        <details className="console-navigation__switcher">
          <summary>تغییر بخش کنسول</summary>
          {links}
        </details>
      )}
    </nav>
  );
}

export function Message(props: {
  readonly kind: "error" | "info";
  readonly children: React.ReactNode;
}) {
  return (
    <div
      role={props.kind === "error" ? "alert" : "status"}
      className={`message ${props.kind}`}
    >
      {props.children}
    </div>
  );
}

export function BusinessConsoleHub(props: {
  readonly context: BusinessContextResponse | null;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  return (
    <props.Shell
      title="کنسول مدیریت قالیشویی"
      description="خدمات، نرخ‌ها، حمل، گزارشات، تیم و پروفایل عملیاتی."
      onAccount={props.onAccount}
      eyebrow="مسیر قالیشویی · کنسول"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsole}
        onNavigate={props.onNavigate}
      />
      <p className="lead">
        این صفحه مرکز ابزارهای مدیریتی قالیشویی است. برای انجام کار، کارت مرتبط
        را انتخاب کنید؛ دکمه بازگشت همیشه شما را به داشبورد اصلی قالیشویی
        می‌برد.
      </p>
    </props.Shell>
  );
}

export function BusinessConsoleReportsPage(props: {
  readonly businessId: string;
  readonly context: BusinessContextResponse | null;
  readonly api: IdentityApiClient;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  const [summary, setSummary] = useState<BusinessReportsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void props.api
      .getBusinessReportsSummary(props.businessId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(identityErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [props.api, props.businessId]);

  return (
    <props.Shell
      title="گزارشات"
      description="خلاصه پذیرش، کارهای فعال و میانگین تخمین پذیرفته‌شده."
      onAccount={props.onAccount}
      eyebrow="کنسول مدیریت"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsoleReports}
        onNavigate={props.onNavigate}
      />
      {error ? <Message kind="error">{error}</Message> : null}
      {summary ? (
        <div className="context-panel">
          <span>پذیرش‌ها: {summary.acceptedCount.toLocaleString("fa-IR")}</span>
          <span>ردها: {summary.rejectedCount.toLocaleString("fa-IR")}</span>
          <span>
            تکمیل‌شده: {summary.completedCount.toLocaleString("fa-IR")}
          </span>
          <span>کارهای فعال: {summary.activeJobs.toLocaleString("fa-IR")}</span>
          <span>
            زمان‌بندی‌شده: {summary.scheduledCount.toLocaleString("fa-IR")}
          </span>
          <span>
            میانگین تخمین پذیرفته:{" "}
            {summary.avgAcceptedEstimate === null
              ? "—"
              : summary.avgAcceptedEstimate.toLocaleString("fa-IR")}
          </span>
          <span>
            امتیاز:{" "}
            {summary.ratingAvg === null ? "—" : summary.ratingAvg.toFixed(1)} (
            {summary.ratingCount.toLocaleString("fa-IR")})
          </span>
        </div>
      ) : error ? null : (
        <Message kind="info">در حال بارگذاری گزارش…</Message>
      )}
    </props.Shell>
  );
}

export function BusinessConsoleRatesPage(props: {
  readonly businessId: string;
  readonly context: BusinessContextResponse | null;
  readonly api: IdentityApiClient;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  const canEdit = hasCap(props.context, "business.settings.manage");
  const [catalog, setCatalog] = useState<BusinessCatalogResponse | null>(null);
  const [draft, setDraft] = useState<BusinessServiceOffering[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [serviceKey, setServiceKey] =
    useState<ServiceCatalogKey>("washing_standard");
  const [carpetType, setCarpetType] = useState<CarpetType>("handmade");
  const [unit, setUnit] = useState<PriceUnit>("per_sqm");
  const [unitPrice, setUnitPrice] = useState("100000");
  const [minPrice, setMinPrice] = useState("0");

  useEffect(() => {
    let cancelled = false;
    void props.api
      .getBusinessCatalog(props.businessId)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        setDraft([...data.offerings]);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(identityErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [props.api, props.businessId]);

  const upsertLocal = () => {
    const next: BusinessServiceOffering = {
      serviceKey,
      carpetType,
      unit,
      unitPrice: Number(unitPrice) || 0,
      minPrice: Number(minPrice) || 0,
      enabled: true,
      currency: "IRR",
    };
    setDraft((prev) => {
      const without = prev.filter(
        (item) =>
          !(
            item.serviceKey === next.serviceKey &&
            item.carpetType === next.carpetType
          ),
      );
      return [...without, next];
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const saved = await props.api.upsertBusinessCatalog(props.businessId, {
        offerings: draft,
      });
      setCatalog(saved);
      setDraft([...saved.offerings]);
      setInfo("کاتالوگ و نرخ‌ها ذخیره شد.");
    } catch (err: unknown) {
      setError(identityErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <props.Shell
      title="خدمات و نرخ‌ها"
      description="فعال‌سازی خدمات کارگاه و نرخ به ازای نوع فرش."
      onAccount={props.onAccount}
      eyebrow="کنسول مدیریت"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsoleRates}
        onNavigate={props.onNavigate}
      />
      {!canEdit ? (
        <Message kind="info">
          فقط مشاهده — برای ویرایش به قابلیت مدیریت تنظیمات نیاز است.
        </Message>
      ) : null}
      {error ? <Message kind="error">{error}</Message> : null}
      {info ? <Message kind="info">{info}</Message> : null}
      <div className="context-panel">
        {draft.length === 0 ? <span>هنوز نرخی تعریف نشده.</span> : null}
        {draft.map((item) => (
          <span key={`${item.serviceKey}-${item.carpetType}`}>
            {SERVICE_LABELS_FA[item.serviceKey]} ·{" "}
            {ITEM_TYPE_LABELS_FA[item.carpetType]} ·{" "}
            {item.unitPrice.toLocaleString("fa-IR")} / {UNIT_LABELS[item.unit]}
            {item.enabled ? "" : " (غیرفعال)"}
            {canEdit ? (
              <>
                {" "}
                <button
                  type="button"
                  className="link-button"
                  onClick={() =>
                    setDraft((prev) =>
                      prev.map((row) =>
                        row.serviceKey === item.serviceKey &&
                        row.carpetType === item.carpetType
                          ? { ...row, enabled: !row.enabled }
                          : row,
                      ),
                    )
                  }
                >
                  {item.enabled ? "غیرفعال" : "فعال"}
                </button>
              </>
            ) : null}
          </span>
        ))}
      </div>
      {canEdit ? (
        <form
          className="identity-form"
          onSubmit={(event) => {
            event.preventDefault();
            upsertLocal();
          }}
        >
          <label>
            خدمت
            <select
              value={serviceKey}
              onChange={(event) =>
                setServiceKey(event.target.value as ServiceCatalogKey)
              }
            >
              {SERVICE_GROUPS.map((group) => (
                <optgroup key={group.id} label={group.labelFa}>
                  {group.keys.map((key) => (
                    <option key={key} value={key}>
                      {SERVICE_LABELS_FA[key]}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label>
            نوع کالا
            <select
              value={carpetType}
              onChange={(event) =>
                setCarpetType(event.target.value as CarpetType)
              }
            >
              {CARPET_TYPES.map((key) => (
                <option key={key} value={key}>
                  {ITEM_TYPE_LABELS_FA[key]}
                </option>
              ))}
            </select>
          </label>
          <label>
            واحد
            <select
              value={unit}
              onChange={(event) => setUnit(event.target.value as PriceUnit)}
            >
              {(Object.keys(UNIT_LABELS) as PriceUnit[]).map((key) => (
                <option key={key} value={key}>
                  {UNIT_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
          <label>
            نرخ واحد (ریال)
            <input
              value={unitPrice}
              onChange={(event) => setUnitPrice(event.target.value)}
            />
          </label>
          <label>
            حداقل قیمت
            <input
              value={minPrice}
              onChange={(event) => setMinPrice(event.target.value)}
            />
          </label>
          <button type="submit">افزودن / به‌روزرسانی در پیش‌نویس</button>
          <button
            type="button"
            disabled={busy || !catalog}
            onClick={() => void save()}
          >
            {busy ? "در حال ذخیره…" : "ذخیره روی سرور"}
          </button>
        </form>
      ) : null}
    </props.Shell>
  );
}

export function BusinessConsoleLogisticsPage(props: {
  readonly businessId: string;
  readonly context: BusinessContextResponse | null;
  readonly api: IdentityApiClient;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  const canEdit = hasCap(props.context, "business.settings.manage");
  const [settings, setSettings] = useState<BusinessLogisticsSettings | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void props.api
      .getBusinessLogistics(props.businessId)
      .then((data) => {
        if (!cancelled) setSettings(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(identityErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [props.api, props.businessId]);

  if (!settings) {
    return (
      <props.Shell
        title="حمل و طبقات"
        description="تنظیم کرایه جمع/تحویل و طبقات."
        onAccount={props.onAccount}
        eyebrow="کنسول مدیریت"
      >
        <ConsoleNav
          current={IDENTITY_WEB_ROUTES.businessConsoleLogistics}
          onNavigate={props.onNavigate}
        />
        {error ? (
          <Message kind="error">{error}</Message>
        ) : (
          <Message kind="info">در حال بارگذاری…</Message>
        )}
      </props.Shell>
    );
  }

  return (
    <props.Shell
      title="حمل و طبقات"
      description="کرایه جمع‌آوری، تحویل و حمل بین‌طبقات."
      onAccount={props.onAccount}
      eyebrow="کنسول مدیریت"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsoleLogistics}
        onNavigate={props.onNavigate}
      />
      {!canEdit ? <Message kind="info">فقط مشاهده.</Message> : null}
      {error ? <Message kind="error">{error}</Message> : null}
      {info ? <Message kind="info">{info}</Message> : null}
      <form
        className="identity-form"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canEdit) return;
          setBusy(true);
          setError(null);
          void props.api
            .updateBusinessLogistics(props.businessId, {
              transportEnabled: settings.transportEnabled,
              pickupFee: settings.pickupFee,
              deliveryFee: settings.deliveryFee,
              roundTripFee: settings.roundTripFee,
              floorCarryEnabled: settings.floorCarryEnabled,
              feePerFloorNoElevator: settings.feePerFloorNoElevator,
              feePerFloorWithElevator: settings.feePerFloorWithElevator,
              maxFloors: settings.maxFloors,
            })
            .then((saved) => {
              setSettings(saved);
              setInfo("تنظیمات حمل ذخیره شد.");
            })
            .catch((err: unknown) => setError(identityErrorMessage(err)))
            .finally(() => setBusy(false));
        }}
      >
        <label>
          <input
            type="checkbox"
            checked={settings.transportEnabled}
            disabled={!canEdit}
            onChange={(event) =>
              setSettings({
                ...settings,
                transportEnabled: event.target.checked,
              })
            }
          />{" "}
          کرایه حمل فعال است
        </label>
        <label>
          جمع‌آوری (ریال)
          <input
            disabled={!canEdit}
            value={String(settings.pickupFee)}
            onChange={(event) =>
              setSettings({
                ...settings,
                pickupFee: Number(event.target.value) || 0,
              })
            }
          />
        </label>
        <label>
          تحویل (ریال)
          <input
            disabled={!canEdit}
            value={String(settings.deliveryFee)}
            onChange={(event) =>
              setSettings({
                ...settings,
                deliveryFee: Number(event.target.value) || 0,
              })
            }
          />
        </label>
        <label>
          رفت‌وبرگشت (اختیاری)
          <input
            disabled={!canEdit}
            value={
              settings.roundTripFee === null
                ? ""
                : String(settings.roundTripFee)
            }
            onChange={(event) =>
              setSettings({
                ...settings,
                roundTripFee:
                  event.target.value.trim() === ""
                    ? null
                    : Number(event.target.value) || 0,
              })
            }
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={settings.floorCarryEnabled}
            disabled={!canEdit}
            onChange={(event) =>
              setSettings({
                ...settings,
                floorCarryEnabled: event.target.checked,
              })
            }
          />{" "}
          حمل بین‌طبقات
        </label>
        <label>
          هر طبقه بدون آسانسور
          <input
            disabled={!canEdit}
            value={String(settings.feePerFloorNoElevator)}
            onChange={(event) =>
              setSettings({
                ...settings,
                feePerFloorNoElevator: Number(event.target.value) || 0,
              })
            }
          />
        </label>
        <label>
          هر طبقه با آسانسور
          <input
            disabled={!canEdit}
            value={String(settings.feePerFloorWithElevator)}
            onChange={(event) =>
              setSettings({
                ...settings,
                feePerFloorWithElevator: Number(event.target.value) || 0,
              })
            }
          />
        </label>
        <label>
          سقف طبقات
          <input
            disabled={!canEdit}
            value={String(settings.maxFloors)}
            onChange={(event) =>
              setSettings({
                ...settings,
                maxFloors: Number(event.target.value) || 1,
              })
            }
          />
        </label>
        {canEdit ? (
          <button type="submit" disabled={busy}>
            {busy ? "در حال ذخیره…" : "ذخیره"}
          </button>
        ) : null}
      </form>
    </props.Shell>
  );
}

export function BusinessConsoleTeamPage(props: {
  readonly businessId: string;
  readonly api: IdentityApiClient;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  const invitableRoles = SYSTEM_ROLE_KEYS.filter(
    (key): key is Exclude<SystemRoleKey, "owner"> => key !== "owner",
  );
  const roleLabels: Record<string, string> = {
    owner: "مالک",
    operations_manager: "مدیر عملیات",
    finance_manager: "حسابدار / مالی",
    workshop_manager: "مدیر کارگاه",
    driver: "راننده",
  };
  const [team, setTeam] = useState<BusinessTeamResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mobile, setMobile] = useState("");
  const [roleKey, setRoleKey] =
    useState<Exclude<SystemRoleKey, "owner">>("operations_manager");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  const reload = async () => {
    setTeam(await props.api.getBusinessTeam(props.businessId));
  };

  useEffect(() => {
    let cancelled = false;
    void props.api
      .getBusinessTeam(props.businessId)
      .then((data) => {
        if (!cancelled) setTeam(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(identityErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [props.api, props.businessId]);

  return (
    <props.Shell
      title="تیم و نقش‌ها"
      description="دعوت کارکنان با نقش و رمز موقت. کارکنان با موبایل و رمز وارد فضای قالیشویی می‌شوند."
      onAccount={props.onAccount}
      eyebrow="کنسول مدیریت"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsoleTeam}
        onNavigate={props.onNavigate}
      />
      {error ? <Message kind="error">{error}</Message> : null}
      {info ? <Message kind="info">{info}</Message> : null}
      <form
        className="identity-form"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          setInfo(null);
          void props.api
            .inviteBusinessTeamMember(props.businessId, {
              mobile,
              roleKey,
              temporaryPassword,
              displayName: displayName.trim() || undefined,
            })
            .then(async () => {
              setInfo("عضو با موفقیت دعوت شد.");
              setMobile("");
              setTemporaryPassword("");
              setDisplayName("");
              await reload();
            })
            .catch((err: unknown) => setError(identityErrorMessage(err)))
            .finally(() => setBusy(false));
        }}
      >
        <strong>دعوت عضو جدید</strong>
        <label>
          نام نمایشی
          <input
            name="new-business-member-name"
            autoComplete="off"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>
        <label>
          موبایل
          <input
            name="new-business-member-mobile"
            inputMode="tel"
            autoComplete="off"
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
            placeholder="09xxxxxxxxx"
            required
          />
        </label>
        <label>
          نقش
          <select
            value={roleKey}
            onChange={(event) =>
              setRoleKey(event.target.value as Exclude<SystemRoleKey, "owner">)
            }
          >
            {invitableRoles.map((key) => (
              <option key={key} value={key}>
                {roleLabels[key] ?? key}
              </option>
            ))}
          </select>
        </label>
        <label>
          رمز موقت
          <input
            type="password"
            name="new-business-member-password"
            autoComplete="new-password"
            value={temporaryPassword}
            onChange={(event) => setTemporaryPassword(event.target.value)}
            minLength={8}
            required
            dir="ltr"
          />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? "در حال ثبت…" : "دعوت و ایجاد رمز"}
        </button>
      </form>
      {team ? (
        <div className="context-panel">
          {team.members.map((member) => (
            <div
              key={member.membershipId}
              style={{ display: "grid", gap: 8, marginBottom: 12 }}
            >
              <span>
                {member.displayName ?? member.mobileE164}
                {member.isPrimaryOwner ? " · مالک اصلی" : ""}
                {" · "}
                {member.roleKeys
                  .map((key) => roleLabels[key] ?? key)
                  .join("، ") || "بدون نقش"}
                {" · "}
                {member.status}
              </span>
              {!member.isPrimaryOwner ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <label
                    style={{ display: "flex", gap: 6, alignItems: "center" }}
                  >
                    نقش
                    <select
                      defaultValue={
                        (member.roleKeys.find((key) => key !== "owner") as
                          Exclude<SystemRoleKey, "owner"> | undefined) ??
                        "operations_manager"
                      }
                      disabled={busy}
                      onChange={(event) => {
                        const nextRole = event.target.value as Exclude<
                          SystemRoleKey,
                          "owner"
                        >;
                        setBusy(true);
                        setError(null);
                        void props.api
                          .updateBusinessTeamMember(
                            props.businessId,
                            member.membershipId,
                            {
                              roleKey: nextRole,
                            },
                          )
                          .then(async () => {
                            setInfo(`نقش «${member.mobileE164}» به‌روز شد.`);
                            await reload();
                          })
                          .catch((err: unknown) =>
                            setError(identityErrorMessage(err)),
                          )
                          .finally(() => setBusy(false));
                      }}
                    >
                      {invitableRoles.map((key) => (
                        <option key={key} value={key}>
                          {roleLabels[key] ?? key}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => {
                      const nextStatus =
                        member.status === "active" ? "inactive" : "active";
                      setBusy(true);
                      setError(null);
                      void props.api
                        .updateBusinessTeamMember(
                          props.businessId,
                          member.membershipId,
                          {
                            status: nextStatus,
                          },
                        )
                        .then(async () => {
                          setInfo(
                            nextStatus === "active"
                              ? `«${member.mobileE164}» فعال شد.`
                              : `«${member.mobileE164}» غیرفعال شد.`,
                          );
                          await reload();
                        })
                        .catch((err: unknown) =>
                          setError(identityErrorMessage(err)),
                        )
                        .finally(() => setBusy(false));
                    }}
                  >
                    {member.status === "active" ? "غیرفعال کردن" : "فعال کردن"}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => {
                      const next = window.prompt(
                        "رمز موقت جدید (حداقل ۸ کاراکتر):",
                      );
                      if (!next || next.trim().length < 8) return;
                      setBusy(true);
                      setError(null);
                      void props.api
                        .resetBusinessTeamMemberPassword(
                          props.businessId,
                          member.membershipId,
                          next.trim(),
                        )
                        .then(() =>
                          setInfo(`رمز «${member.mobileE164}» ریست شد.`),
                        )
                        .catch((err: unknown) =>
                          setError(identityErrorMessage(err)),
                        )
                        .finally(() => setBusy(false));
                    }}
                  >
                    ریست رمز
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : error ? null : (
        <Message kind="info">در حال بارگذاری تیم…</Message>
      )}
    </props.Shell>
  );
}

export function BusinessConsoleSettingsPage(props: {
  readonly businessId: string;
  readonly context: BusinessContextResponse | null;
  readonly api: IdentityApiClient;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  const canEdit = hasCap(props.context, "business.settings.manage");
  const [profile, setProfile] = useState<BusinessProfileResponse | null>(null);
  const [autoQuote, setAutoQuote] = useState(false);
  const [autoSchedule, setAutoSchedule] = useState(false);
  const [leadHours, setLeadHours] = useState("24");
  const [pickupHour, setPickupHour] = useState("10");
  const [returnDays, setReturnDays] = useState("3");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    void props.api
      .getBusinessProfile(props.businessId)
      .then((value) => {
        setProfile(value);
        setAutoQuote(value.autoQuoteEnabled);
        setAutoSchedule(value.autoScheduleEnabled);
        setLeadHours(String(value.pickupLeadHours));
        setPickupHour(String(value.preferredPickupHour));
        setReturnDays(String(value.typicalReturnDays ?? 3));
      })
      .catch((err: unknown) => setError(identityErrorMessage(err)));
  }, [props.api, props.businessId]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const saved = await props.api.updateBusinessProfile(props.businessId, {
        autoQuoteEnabled: autoQuote,
        autoScheduleEnabled: autoSchedule,
        pickupLeadHours: Number(leadHours),
        preferredPickupHour: Number(pickupHour),
        typicalReturnDays: Number(returnDays),
      });
      setProfile(saved);
      setInfo("قواعد هوشمند قیمت و جمع‌آوری ذخیره شد.");
    } catch (err: unknown) {
      setError(identityErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <props.Shell
      title="تنظیمات"
      description="انتخاب کنید قیمت و زمان همان لحظه از روی تعرفه‌ها محاسبه شود یا با تأیید طرفین پیش برود."
      onAccount={props.onAccount}
      eyebrow="کنسول مدیریت"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsoleSettings}
        onNavigate={props.onNavigate}
      />
      {!canEdit ? (
        <Message kind="info">
          فقط مالک یا مدیر دارای دسترسی تنظیمات می‌تواند این بخش را تغییر دهد.
        </Message>
      ) : null}
      {error ? <Message kind="error">{error}</Message> : null}
      {info ? <Message kind="info">{info}</Message> : null}
      <form
        className="identity-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label>
          <input
            type="checkbox"
            checked={autoQuote}
            disabled={!canEdit || busy}
            onChange={(event) => setAutoQuote(event.target.checked)}
          />
          اعلام خودکار قیمت قطعی از روی تعرفه‌ها و هزینه حمل
        </label>
        <label>
          <input
            type="checkbox"
            checked={autoSchedule}
            disabled={!canEdit || busy}
            onChange={(event) => setAutoSchedule(event.target.checked)}
          />
          پیشنهاد خودکار زمان جمع‌آوری و بازگشت
        </label>
        <label>
          فاصله آماده‌سازی تا جمع‌آوری (ساعت)
          <input
            type="number"
            min={1}
            max={336}
            value={leadHours}
            disabled={!canEdit || busy}
            onChange={(event) => setLeadHours(event.target.value)}
          />
        </label>
        <label>
          ساعت ترجیحی جمع‌آوری
          <input
            type="number"
            min={7}
            max={21}
            value={pickupHour}
            disabled={!canEdit || busy}
            onChange={(event) => setPickupHour(event.target.value)}
          />
        </label>
        <label>
          زمان معمول بازگشت (روز)
          <input
            type="number"
            min={1}
            max={14}
            value={returnDays}
            disabled={!canEdit || busy}
            onChange={(event) => setReturnDays(event.target.value)}
          />
        </label>
        <div className="context-panel">
          <span>
            قیمت:{" "}
            {autoQuote
              ? "خودکار و قابل مشاهده از زمان پیشنهاد"
              : "نیازمند رفت‌وبرگشت و تأیید مشتری"}
          </span>
          <span>
            زمان:{" "}
            {autoSchedule
              ? "محاسبه خودکار بر اساس ظرفیت تعریف‌شده"
              : "نیازمند پیشنهاد و تأیید طرف مقابل"}
          </span>
          <span>
            وضعیت ذخیره: {profile ? "همگام با سرور" : "در حال دریافت…"}
          </span>
        </div>
        {canEdit ? (
          <button type="submit" disabled={busy}>
            {busy ? "در حال ذخیره…" : "ذخیره قواعد هوشمند"}
          </button>
        ) : null}
      </form>
    </props.Shell>
  );
}
